/* eslint-disable react-refresh/only-export-components */
import './configurations/i18n.ts'
import { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { Router, RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import './index.css'
import { ThemeProvider } from './components/theme-provider.tsx'
import { AuthProvider, useAuth } from './context/auth/AuthContext.tsx'
import { Toaster } from "@/components/ui/sonner"
import { AppProvider } from './context/app/AppContext.tsx'
import Loader, { loaderRef } from './components/ui/loader.tsx'
import { UserProvider } from './context/user/UserContext.tsx'
import { GlobalProvider } from './context/GlobalContext.tsx'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AbilityProvider, useAbility } from './context/auth/AbilityContext.tsx'
import * as Sentry from "@sentry/react";
import LocalizeProvider from './context/locale/LocalizeProvider.tsx'
import { NotificationProvider } from './context/notification/NotificationContext.tsx'
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';

// Sentry should only be initialized once, even with hot reloads.
if (
  process.env.NODE_ENV === 'production' &&
  !(window as any).__sentry_initialized
) {
  Sentry.init({
    dsn: "https://cd1fc78520d6a3fe27a826bed8a44a2b@sentry.trabilisim.tech/6",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // Sadece Sentry'nin kendi endpoint'lerine trace header'ları gönder, API'lere gönderme
    tracePropagationTargets: [],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
  (window as any).__sentry_initialized = true;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof Router
  }
}

const queryClient = new QueryClient()

const RouterSetup = () => {
  const authContext = useAuth()
  const abilityContext = useAbility()

  useEffect(() => {
    if (authContext.isLoading) {
      loaderRef.current?.incLoader()
    } else {
      loaderRef.current?.decLoader()
    }
  }, [authContext.isLoading])

  const router = createRouter({
    routeTree,
    context: {
      auth: authContext,
      ability: abilityContext as any,
      queryClient: queryClient,
    }
  })
  return (
    <RouterProvider router={router} />
  )
}


function composeProviders(...providers: Array<[React.ComponentType<any>, any?]>) {
  return ({ children }: { children: React.ReactNode }) =>
    providers.reduceRight(
      (acc, [Provider, props]) => <Provider {...props}>{acc}</Provider>,
      children
    );
}

const AppProviders = composeProviders(
  [ThemeProvider, { defaultTheme: "dark", storageKey: "theme" }],
  [QueryClientProvider, { client: queryClient }],
  [AbilityProvider],
  [LocalizeProvider],
  [AuthProvider],
  [UserProvider],
  [GlobalProvider],
  [AppProvider],
  [NotificationProvider]
);


const rootElement = document.getElementById('root')!
//test kodları için eklendi
if (rootElement && (!rootElement.innerHTML || rootElement.innerHTML.trim() === '' || rootElement.innerHTML.trim() === '<!-- test root -->')) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    // <StrictMode>
    <AppProviders>
      <RouterSetup />
      <Toaster position="top-right" expand swipeDirections={['right']} />
      <Loader variant='linear' />
      <TanStackDevtools
        config={{ openHotkey: [] }}
        plugins={[
          {
            name: 'TanStack Query',
            render: <ReactQueryDevtoolsPanel />,
          },
        ]}
      />
    </AppProviders>
    // </StrictMode>
    ,
  )
}

